import { DonutChart as BaseDonutChart } from "@mantine/charts"
import { Button, Flex, Stack } from "@mantine/core"
import { useElementSize } from "@mantine/hooks"
import { useMemo } from "react"

import { Filters } from "@costory/types/filters"
import { ExplorerChartType } from "@costory/types/prisma-client"

import { createDonutChartDataWithConsistentColors } from "@costory/shared/utils/consistentColors"
import { isGroupByOptional } from "@costory/shared/utils/filters"
import { formatNumber } from "@costory/shared/utils/format"

import { TrendBadge } from "@costory/front/components/TrendBadge"
import { ChartTooltip } from "@costory/front/components/charts/ChartTooltip"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import {
  useWaterfallQuery,
  useExplorerQuery,
} from "@costory/front/queries/explore"

type Props = {
  filters: Filters
  height?: number
  displayTrend?: boolean
  drillDownInto?: (value: string, mode: "sameWindow" | "newTab") => void
  setExplorerChartType?: (chartType: ExplorerChartType) => void
  isAnimationActive?: boolean
}

export const DonutChart = ({
  filters,
  height,
  displayTrend,
  drillDownInto,
  setExplorerChartType,
  isAnimationActive = true,
}: Props) => {
  const waterfallQuery = useWaterfallQuery({ ...filters })
  const explorerQuery = useExplorerQuery(filters, true) // Get stacked bar chart data for color mapping
  const { ref, width, height: containerHeight } = useElementSize()
  const computedSize = useMemo(() => {
    const defaultSize = 300
    const availableWidth = width || defaultSize
    const availableHeight = containerHeight || height || defaultSize
    const maxSize = height || defaultSize

    return Math.min(availableWidth, availableHeight, maxSize)
  }, [width, containerHeight, height])
  return (
    <QueryWrapper query={waterfallQuery}>
      {({ data: waterfallData }) => (
        <QueryWrapper query={explorerQuery}>
          {({ data: explorerData }) => {
            // Create donut chart data with consistent colors using shared utility
            const donutDataWithColors =
              createDonutChartDataWithConsistentColors(
                waterfallData.donutChartData.arr,
                explorerData.stackedBarChartData.items,
              ).map((elem) => ({
                ...elem,
                onClick: () => {
                  if (drillDownInto) {
                    drillDownInto(elem.name, "sameWindow")
                  }
                },
              }))

            return (
              <Stack
                h="100%"
                w="100%"
                ref={ref}
                style={{
                  minHeight: height ? `${height}px` : "200px",
                  position: "relative",
                }}
              >
                {displayTrend && (
                  <Flex justify="end" mb="lg">
                    <Button
                      bd="none"
                      bg="transparent"
                      onClick={() => {
                        setExplorerChartType?.(ExplorerChartType.WATERFALL)
                      }}
                      data-intercom-target="costory-tour-explorer-donut-percentage-button"
                    >
                      <TrendBadge
                        current={waterfallData.donutChartData.total}
                        previous={waterfallData.donutChartData.previous}
                      />
                    </Button>
                  </Flex>
                )}
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "visible",
                  }}
                >
                  <BaseDonutChart
                    size={computedSize}
                    thickness={30}
                    styles={{
                      label: {
                        fontSize: Math.max(
                          18,
                          Math.min(36, computedSize * 0.15),
                        ),
                        fontWeight: "bold",
                      },
                      ...(isAnimationActive
                        ? {}
                        : {
                            root: {
                              "& *": {
                                animation: "none !important",
                                transition: "none !important",
                              },
                            },
                          }),
                    }}
                    chartLabel={formatNumber({
                      value: waterfallData.donutChartData.total,
                      currency: filters.currency,
                    })}
                    valueFormatter={(value) =>
                      formatNumber({ value, currency: filters.currency })
                    }
                    data={donutDataWithColors}
                    tooltipProps={{
                      content: ({ label, payload }) => (
                        <ChartTooltip
                          label={label}
                          payload={
                            payload as {
                              name: string
                              value: number
                              dataKey: string
                            }[]
                          }
                          tooltipData={waterfallData.dataGroupedByValue}
                          currency={filters.currency}
                          groupByOptional={isGroupByOptional(filters.groupBy)}
                          isDonutChart={true}
                        />
                      ),
                    }}
                  />
                </div>
              </Stack>
            )
          }}
        </QueryWrapper>
      )}
    </QueryWrapper>
  )
}
